/**
 * PinSetup: needs_pin state, right after a code-verify for a nurse who has
 * never set a PIN (or just had one reset). POST /api/auth/pin-set.
 */
import { useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, FormField } from '@ecmb/ui';
import type { ApiClient } from '../api.js';
import { pinSetupCopy, sharedCopy } from '../copy.js';
import { pinLengthOk, weakPin } from '../../shared/pin-rules.js';

export type PinSetupProps = {
  api: ApiClient;
  nurseName: string;
  /** Fires on a successful pin-set (200); caller re-fetches auth state. */
  onDone: () => void;
};

export function PinSetup({ api, nurseName, onDone }: PinSetupProps) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);

  /**
   * ⭐⭐ T1749 M9 (DS-27) — THIS SCREEN GAINS THE FOCUS-ON-REFUSAL ITS TWO
   * SIBLINGS ALREADY HAD, AND THE GAIN IS THE CHANGE RATHER THAN A SIDE EFFECT
   * OF THE ADOPTION.
   *
   * ⛔ WHAT WAS HERE BEFORE. Every one of the five refusal branches below called
   * `setError` and moved NOTHING: focus stayed on the Save PIN button the nurse
   * pressed, and the message rendered as a bare `<p class="field-error">` with no
   * `id` and no `aria-describedby` from either box — so neither input was marked
   * invalid and neither pointed at the message. `Enrol.tsx`'s `refuse()` and
   * `Picker.tsx`'s `refusePin()` (II-portal-#21 · ADR4-D10(b)) both did both
   * halves; this screen was the one auth surface that did neither, which is why
   * ADR4-D10(b) reading as IMPLEMENTED was only ever true of two screens out of
   * three.
   *
   * ⛔ AND IT IS A PRECONDITION OF THE ADOPTION, NOT A BONUS. `FormField`'s
   * message carries no `role="alert"` by design and is announced through
   * `aria-describedby`, which is read when something LANDS on the control. Moving
   * this screen's error into that slot WITHOUT adding the focus would have traded
   * a message that at least interrupted for one that announced nothing at all —
   * so the focus and the `error` prop land together or not at all.
   *
   * ⚠️ ALWAYS THE FIRST BOX, INCLUDING ON A MISMATCH, and that is deliberate
   * rather than sloppy. A mismatch is a property of the PAIR, not a fault in the
   * second box; re-entering a PIN starts at the first field, and sending her to
   * "Enter it again" would tell her the box she typed second was the wrong one.
   */
  function refuse(message: string): void {
    setError(message);
    pinRef.current?.focus();
  }

  async function save(): Promise<void> {
    if (!pinLengthOk(pin)) {
      refuse(pinSetupCopy.invalidError);
      return;
    }
    if (weakPin(pin)) {
      refuse(pinSetupCopy.weakError);
      return;
    }
    if (pin !== confirm) {
      refuse(pinSetupCopy.mismatchError);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await api.post('/api/auth/pin-set', { pin });
    setBusy(false);
    if (res.kind === 'network-error') {
      refuse(sharedCopy.networkError);
      return;
    }
    if (res.status === 200) {
      onDone();
      return;
    }
    refuse(sharedCopy.genericError);
  }

  function onSubmit(e: JSX.TargetedEvent<HTMLFormElement, Event>): void {
    e.preventDefault();
    void save();
  }

  return (
    <div class="screen pin-setup-screen">
      <h1>{pinSetupCopy.title}</h1>
      <p>
        {nurseName}, {pinSetupCopy.intro}
      </p>
      {/* ⭐ `shape="cover"` MATCHES THE KIOSK SIGN-IN (`quote-helper/src/client/screens/Login.tsx`).
          The two tools' sign-in screens are the pair a reviewer puts side by side and asks whether
          they read as one system, and a different card padding is exactly the difference she would
          have to name. Ruled at T1749 M9 (DS-27); `card` (the default) is what the first pass left. */}
      <Card shape="cover">
        <form onSubmit={onSubmit}>
          {/* ⛔ THE MESSAGE HANGS OFF THE FIRST BOX, WHICH IS THE ONE `refuse()`
              FOCUSES. The two must be the same field or the announcement breaks:
              `FormField` derives the message's id from the control's own and
              points that control's `aria-describedby` at it, and a reader only
              hears it when focus lands there. Both boxes keep `pattern` — the
              digits-only pre-flight this screen has always had, and the reason
              `FormField` gained the prop at M9 rather than the fields losing it. */}
          <FormField
            id="pinsetup-pin"
            label={pinSetupCopy.pinLabel}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            value={pin}
            onInput={setPin}
            required
            error={error ?? undefined}
            elementRef={pinRef}
          />
          <FormField
            id="pinsetup-confirm"
            label={pinSetupCopy.confirmLabel}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            value={confirm}
            onInput={setConfirm}
            required
          />
          <button type="submit" class="btn btn--primary" disabled={busy}>
            {pinSetupCopy.saveButton}
          </button>
        </form>
      </Card>
    </div>
  );
}
